import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { Box, Grid, LinearProgress } from '@mui/material';
import moment from 'moment';

import { LayoutDashboard } from '../../../shared/layouts';
import { GenericService } from '../../../shared/services';
import { TestPackageMaintenance, TestScenario } from '../..';
import { TestPackageDTO, TestPackageFilterDTO, ValueListDTO, ValueListFilterDTO, WorkspaceDTO } from '../../../shared/interfaces';
import { DatePickers, ISnackbarAlert, SelectField, SnackbarAlert, CardList, TextFieldInput, ToolBar, IDialogActions, ICardActions, ICardColumns, IChipFilter } from '../../../shared/components';
import { Delete } from '../../../shared/components/confirm-delete/ConfirmDelete';
import { ListId, LocalStorange } from '../../../shared/environment';
import { WorkspaceFilterDTO } from '../../../shared/interfaces/testingSphere/WorkspaceFilterDTO';

interface ITestPackageProps {
    data: any
}
export const TestPackage: React.FC<ITestPackageProps> = ({ data }) => {

    const [workspaceList, setWorkspaceList] = useState<WorkspaceDTO[]>([]);
    const [testLevelList, setTestLevelList] = useState<ValueListDTO[]>([]);
    const [executionStatusList, setExecutionStatusList] = useState<ValueListDTO[]>([]);

    const [testPackageFilterDTO, setTestPackageFilterDTO] = useState<TestPackageFilterDTO>();
    const [testPackageList, setTestPackageList] = useState<TestPackageDTO[]>([]);

    const [actionExecuted, setActionExecuted] = useState(false);
    const [newRecord, setNewRecord] = useState('');
    const [editRecord, setEditRecord] = useState('');
    const [deleteRecord, setDeleteRecord] = useState('');
    const [expadedData, setExpandedData] = useState('');
    const [isOpenMaintenance, setIsOpenMaintenance] = useState(false);

    const [loading, setLoading] = useState(false);
    const [snackBarMessage, setSnackBarMessage] = useState<ISnackbarAlert>();
    const [isOpenSnackBar, setIsOpenSnackBar] = useState(false);

    const userInfo = JSON.parse(localStorage.getItem(LocalStorange.USERINFO) || '');

    useEffect(() => {
        setTestPackageFilterDTO({
            ...testPackageFilterDTO,
            name: '',
            workspaceId: data.id ?? ''
        } as TestPackageFilterDTO);

        handleWorkspaceGetAllAsync();
        handleListGetAll(ListId.testingsphere.testpackage.testlevel, setTestLevelList);
        handleListGetAll(ListId.testingsphere.testpackage.executionstatus, setExecutionStatusList);

    }, []);

    useEffect(() => {
        const interval = setInterval(() => {
            handleTestPackageGetAllViewAsync();
        }, 5000);
        return () => clearInterval(interval);
    }, [testPackageFilterDTO]);

    useEffect(() => {
        handleTestPackageGetAllViewAsync();
    }, [testPackageFilterDTO]);

    useEffect(() => {
        if (actionExecuted)
            handleTestPackageGetAllViewAsync();
    }, [actionExecuted]);

    const handleListGetAll = useCallback(async (listId: string, setList: any, orderBy?: 'valueIndex ASC') => {
        const valueListFilterDTO = {
            value: '',
            listId: listId,
            orderBy: orderBy
        };
        GenericService.GetAllAsync1(
            'parmsphere.valueList',
            valueListFilterDTO,
            setLoading,
            setList,
            showMessage
        );
    }, []);

    const handleWorkspaceGetAllAsync = useCallback(async () => {
        const workspaceFilterDTO: WorkspaceFilterDTO = {
            name: '',
            companyId: userInfo.user.companyId,
        };
        GenericService.GetAllAsync1('testingSphere.workspace', workspaceFilterDTO, setLoading, setWorkspaceList, showMessage);
    }, []);

    const handleTestPackageGetAllViewAsync = useCallback(async () => {
        if (testPackageFilterDTO === undefined)
            return;

        GenericService.GetAllViewAsync1('testingSphere.testPackage', testPackageFilterDTO, setActionExecuted, setLoading, setTestPackageList, showMessage, true);

    }, [testPackageFilterDTO]);


    const handleClearFilterClick = () => {
        setTestPackageFilterDTO({ 
            ...testPackageFilterDTO, 
            name: '', 
            workspaceId: '', 
            testLevelId: '', 
            executionStatusId: '', 
            startExecutionDateTime: undefined, 
            endExecutionDateTime: undefined });
    };

    function handleSearchClick() {
        handleTestPackageGetAllViewAsync();
    }

    const handleNew = () => {
        setNewRecord('data');
        setEditRecord('');
        setDeleteRecord('');
        setIsOpenMaintenance(true);
    };

    const handleViewDetatils = (data: string) => {
        setNewRecord('');
        setEditRecord(data);
        setDeleteRecord('');
        setIsOpenMaintenance(true);
    };

    const handleDelete = (data: string) => {
        setNewRecord('');
        setDeleteRecord(data);
        setEditRecord('');
        setIsOpenMaintenance(true);
    };

    const handleStartExecutionAsync = (data: any) => {
        const testPackageFilterDTO: TestPackageFilterDTO = {
            id: data.id
        };
        GenericService.PostGenericAsync1('testingsphere.testPackage', 'StartExecutionAsync', testPackageFilterDTO, setActionExecuted, setLoading, showMessage);
        handleTestPackageGetAllViewAsync();
    };

    const handleStopExecutionAsync = (data: any) => {
        const testPackageFilterDTO: TestPackageFilterDTO = {
            id: data.id
        };
        GenericService.PostGenericAsync1('testingsphere.testPackage', 'StopExecutionAsync', testPackageFilterDTO, setActionExecuted, setLoading, showMessage);
        handleTestPackageGetAllViewAsync();
    };

    const handleCloneTestPackage = (data: any) => {
        const testPackageFilterDTO: TestPackageFilterDTO = {
            id: data.id
        };
        GenericService.PostGenericAsync1('testingsphere.testPackage', 'CloneAsync', testPackageFilterDTO, setActionExecuted, setLoading, showMessage);
    };

    const toolBarActions: IDialogActions[] = [
        { id: 'new', text: 'New TestPackage', icon: 'add_circle_outlined', iconColor: 'primary', setAction: handleNew },
    ];

    const cardActions: ICardActions[] = [
        { id: 'viewDetails', toolTip: 'View Details', icon: 'preview_outlined', iconColor: 'primary', setAction: handleViewDetatils },
        { id: 'delete', toolTip: 'Delete', icon: 'delete_outlined', iconColor: 'error', setAction: handleDelete },
        { id: 'cloneTestPackage', toolTip: 'Clone TestPackage', icon: 'file_copy_outlined', iconColor: 'info', setAction: handleCloneTestPackage },
        { id: 'ExecutionAction', toolTip: 'Start Execution', icon: 'play_circle_outlined', iconColor: 'info', setAction: handleStartExecutionAsync },
        { id: 'ExecutionAction', toolTip: 'Stop Execution', icon: 'stop_circle_outlined', iconColor: 'warning', setAction: handleStopExecutionAsync },
        // { id: 'viewTestScenarioExecutionTimeline', toolTip: 'View TestScenario Execution Timeline', icon: 'dynamic_feed_outlined', iconColor: 'info', setAction: handleViewTestScenarioExecutionTimeline },
        // { id: 'viewExecutionReport', toolTip: 'View Execution Report', icon: 'summarize_outlined', iconColor: 'info', setAction: handleViewExecutionReprt },
    ];

    const chipFilters: IChipFilter[] = [
        { label: 'Total Records', value: testPackageList.length },
        { label: 'Start Execution', value: testPackageFilterDTO?.startExecutionDateTime ? moment(testPackageFilterDTO.startExecutionDateTime).format('DD/MM/YYYY') : '' },
        { label: 'End Execution', value: testPackageFilterDTO?.endExecutionDateTime ? moment(testPackageFilterDTO.endExecutionDateTime).format('DD/MM/YYYY') : '' },
        { label: 'Workspace', value: workspaceList.find(x => x.id === testPackageFilterDTO?.workspaceId)?.name },
        { label: 'Test Level', value: testLevelList.find(x => x.id === testPackageFilterDTO?.testLevelId)?.value },
        { label: 'Status Execution', value: executionStatusList.find(x => x.id === testPackageFilterDTO?.executionStatusId)?.value },
        { label: 'TestPackage Name', value: testPackageFilterDTO?.name },
    ];

    const columns: ICardColumns[] = [
        { field: 'name', label: 'Name', size: 4, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'executionStatus', label: 'Execution Status', size: 1, type: 'text', visible: true, isChip: true, hasFieldLine: false },
        { field: 'startExecutionDateTime', label: 'Start Execution', size: 2, type: 'dateTime', visible: true, isChip: false, hasFieldLine: false },
        { field: 'endExecutionDateTime', label: 'End Execution', size: 2, type: 'dateTime', visible: true, isChip: false, hasFieldLine: false },
        { field: 'elapseTime', label: 'ElapseTime', size: 1, type: 'text', visible: true, isChip: false, hasFieldLine: false },
        { field: 'orderExecution', label: 'Order Execution', size: 1, type: 'number', visible: true, isChip: false, hasFieldLine: false },
        { field: 'status', label: 'Enable to Run', size: 1, type: 'text', visible: true, isChip: true, hasFieldLine: false },
        { field: 'totalScenarios', label: 'Total Scenarios', size: 1, type: 'number', visible: true, isChip: false, hasFieldLine: false },
        { field: 'totalExecutedScenarios', label: 'Executed Scenarios', size: 1, type: 'number', visible: true, isChip: false, hasFieldLine: false },
        { field: 'TotalScenariosNoRun', label: 'Scenarios No Run', size: 1, type: 'number', visible: true, isChip: false, hasFieldLine: false },
        { field: 'TotalScenariosPending', label: 'Scenarios Pending', size: 1, type: 'number', visible: true, isChip: false, hasFieldLine: false },
        { field: 'TotalScenariosRunning', label: 'Scenarios Running', size: 1, type: 'number', visible: true, isChip: false, hasFieldLine: false },
        { field: 'TotalScenariosPassed', label: 'Scenarios Passed', size: 1, type: 'number', visible: true, isChip: false, hasFieldLine: false, colorValue: 'green' },
        { field: 'TotalScenariosFailed', label: 'Scenarios Failed', size: 1, type: 'number', visible: true, isChip: false, hasFieldLine: false, colorValue: 'red' },
        { field: 'TotalScenariosBlocked', label: 'Scenarios Blocked', size: 1, type: 'number', visible: true, isChip: false, hasFieldLine: false, colorValue: 'yellow' },
        { field: 'TotalScenariosWarning', label: 'Scenarios Warning', size: 1, type: 'number', visible: true, isChip: false, hasFieldLine: false },
    ];

    function showMessage(message: string, type: 'error' | 'info' | 'success' | 'warning') {
        const msg: ISnackbarAlert = {
            id: 'message',
            message: message,
            type: type,
        };
        setSnackBarMessage(msg);
        setIsOpenSnackBar(true);
    }
    return (
        <React.Fragment>
            {isOpenSnackBar && (
                <SnackbarAlert
                    snackBarAlert={snackBarMessage}
                    setIsOpen={setIsOpenSnackBar}
                    isOpen={isOpenSnackBar}
                />
            )}
            <LayoutDashboard title='TESTPACKAGE'>
                <ToolBar
                    clickSearch={handleSearchClick}
                    clickClearFilter={handleClearFilterClick}
                    chipFilters={chipFilters}
                    actions={toolBarActions}
                >
                    <Grid container spacing={1}>
                        <Grid item xs={12} md={4}>
                            <SelectField
                                id='workspaceId'
                                label='Workspace'
                                value={testPackageFilterDTO?.workspaceId}
                                setValue={setTestPackageFilterDTO}
                                obj={testPackageFilterDTO}
                                valueList={workspaceList}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextFieldInput
                                id='name'
                                label='Test Package name'
                                value={testPackageFilterDTO?.name}
                                setValue={setTestPackageFilterDTO}
                                obj={testPackageFilterDTO}
                                placeholder={'TestPackage Name'}
                                maxLength={36}
                            />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <DatePickers
                                id='startExecutionDateTime'
                                label='Start Execution'
                                value={testPackageFilterDTO?.startExecutionDateTime ?? null}
                                setValue={setTestPackageFilterDTO}
                                obj={testPackageFilterDTO}
                            />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <DatePickers
                                id='endExecutionDateTime'
                                label='End Execution'
                                value={testPackageFilterDTO?.endExecutionDateTime ?? null}
                                setValue={setTestPackageFilterDTO}
                                obj={testPackageFilterDTO}
                            />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <SelectField
                                id='testLevelId'
                                label='Test Level'
                                value={testPackageFilterDTO?.testLevelId}
                                setValue={setTestPackageFilterDTO}
                                obj={testPackageFilterDTO}
                                valueList={testLevelList}
                                hasNoneSelect={true}
                                labelKey='value'
                                valueKey='id'
                                searchable={true}
                            />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <SelectField
                                id='executionStatusId'
                                label='Execution Status'
                                value={testPackageFilterDTO?.executionStatusId}
                                setValue={setTestPackageFilterDTO}
                                obj={testPackageFilterDTO}
                                valueList={executionStatusList}
                                hasNoneSelect={true}
                            />
                        </Grid>
                    </Grid>
                </ToolBar>
                {loading && (
                    <LinearProgress sx={{ borderRadius: 3, mt: - 0.7, mb: 0.2 }} />
                )}
                <Box sx={{ display: 'flex', maxHeight: '80vh', width: '100%', overflow: 'hidden', overflowY: 'auto' }}>
                    <CardList
                        columns={columns}
                        rows={testPackageList}
                        actions={cardActions}
                        hasChildren={true}
                        setRowExpanded={setExpandedData}
                        cardSize={12}
                        childrenTitle={'TEST SCENARIO'}
                        childrenComponent={(
                            <Box width={'100%'}>
                                <TestScenario
                                    data={expadedData}
                                />
                            </Box>
                        )}
                    />
                </Box>
                {newRecord && (
                    <TestPackageMaintenance
                        data={''}
                        setIsOpenMaintenance={setIsOpenMaintenance}
                        isOpenMaintenance={isOpenMaintenance}
                        setActionExecuted={setActionExecuted}
                        parentId={data.id ?? testPackageFilterDTO?.workspaceId}
                    />
                )}
                {editRecord && (
                    <TestPackageMaintenance
                        data={editRecord}
                        setIsOpenMaintenance={setIsOpenMaintenance}
                        isOpenMaintenance={isOpenMaintenance}
                        setActionExecuted={setActionExecuted}
                    />
                )}
                {deleteRecord && (<Delete
                    entity={'testingsphere.testPackage'}
                    data={deleteRecord}
                    setIsOpenDiaglog={setIsOpenMaintenance}
                    isOpenDialog={isOpenMaintenance}
                    setActionExecuted={setActionExecuted}
                />)}
            </LayoutDashboard>
        </React.Fragment >
    );
};